import { IsPhaseAtLeast, IsWindowOpen, PublishedResources, StageOf, type IOEPResource, type IOEPSeasonTimeline, type OEPPhase } from 'agu_oep_core';
import type { FeedbackPlatformParticipantEntity } from 'mj_generatedentities';
import { CRITERION_SHORT, ClockET, DayMonth, Size } from '../shared/format';
import { Up } from '../shared/rows';

/** A career-stage value Participant.CareerStage accepts (the generated value list = CK_Participant_CareerStage). */
export type OEPCareerStage = NonNullable<FeedbackPlatformParticipantEntity['CareerStage']>;

/** logic.js:64 CAREER_STAGES, in the prototype's order; each value is one the CHECK list accepts (typed by OEPCareerStage). */
export const OEP_CAREER_STAGES: readonly OEPCareerStage[] = ['Undergraduate', 'Master’s student', 'PhD student', 'Postdoctoral researcher', 'Early career professional'];

/** Blocked draft panel hint and chip status (client edit 2026-10-20). */
export const OEP_CAREER_FIRST = 'You must select your career stage before you can upload your draft.';

export function IsOEPCareerStage(v: string | null): v is OEPCareerStage {
    return v !== null && (OEP_CAREER_STAGES as readonly string[]).includes(v);
}

const THREAD_LIMIT = 3;

export interface OEPPresentationFile { FileID: string; Name: string; SizeBytes: number | null; UploadedAt: Date }
export interface OEPThreadMessageInput { SenderUserID: string; Body: string }
export interface OEPReviewScoreInput { Sequence: number; Level: number | null; LevelLabel: string; Text: string }
export interface OEPReviewInput {
    ReviewID: string; ReviewerName: string; Institution: string | null; ReleasedOn: Date | null;
    Scores: OEPReviewScoreInput[];
    Strengths: string; Suggestions: string;
    ConversationID: string | null; Thread: OEPThreadMessageInput[]; ThankYouSent: boolean;
}

export interface PresentationInputs {
    Phase: OEPPhase; Timeline: IOEPSeasonTimeline; SeasonCode: string; Me: string;
    Presentation: { ID: string; Title: string; SectionName: string; Format: string | null; Code: string | null };
    CareerStage: string | null;
    Draft: OEPPresentationFile | null;
    Final: OEPPresentationFile | null;
    Reviews: OEPReviewInput[];
    /** null until phase Complete (Committee Selection Items are visible to the presenter only then, T5). */
    IsWinner: boolean | null;
    Resources: IOEPResource[];
}

export type OEPTone = 'ok' | 'warn' | 'hot' | 'neutral';
/** Replace = the drop shown after "Replace file" (same copy as the first upload); null when the window is closed. */
export interface OEPFileRowVM { FileID: string; Name: string; Detail: string; CanReplace: boolean; Replace: { Title: string; Note: string } | null }
export interface OEPThreadMessageVM { Who: string; Body: string; Mine: boolean }
export interface FeedbackItemVM {
    ReviewID: string; ConversationID: string | null;
    Who: string; Institution: string | null; Position: string; When: string;
    Scores: { Label: string; Value: string; Text: string }[];
    Strengths: string; Suggestions: string | null;
    ThreadNote: string; Thread: OEPThreadMessageVM[]; CanWrite: boolean;
    ThanksTitle: string; ThanksNote: string; ThanksSent: boolean; CanThank: boolean;
}

export interface PresentationVM {
    Title: string;
    Intro: string;
    Eyebrow: string;
    DraftStatus: { Label: string; Tone: OEPTone };
    HeadStart: { Text: string; Links: IOEPResource[] } | null;
    CareerAsk: { Stages: readonly OEPCareerStage[]; Chosen: OEPCareerStage | null; Note: string } | null;
    DraftDrop: { Title: string; Note: string; Disabled: boolean; Hint: string | null } | null;
    DraftFile: (OEPFileRowVM & { Note: string }) | null;
    ClosedNote: string | null;
    Final: { DeadlineNote: string; Intro: string; Drop: { Title: string; Note: string } | null; Missed: boolean; File: OEPFileRowVM | null } | null;
    Feedback: { CountLabel: string; Intro: string; Items: FeedbackItemVM[] } | null;
    /** Null while the feedback-on-draft guidance has no published link (client edit 2026-10-27). */
    Guidance: { Title: string; Text: string; Link: IOEPResource } | null;
    Outcome: { Title: string; Body: string } | null;
}


/**
 * markup.html:349 "8.4 MB · uploaded 28 October, 4:12 p.m. ET". MJ: Files stores no size, so the size
 * part appears only when known (a file uploaded in this session); without it the line starts "Uploaded".
 */
export function OEPFileDetail(f: OEPPresentationFile): string {
    const when = `${DayMonth(f.UploadedAt)}, ${ClockET(f.UploadedAt)}`;
    return f.SizeBytes !== null ? `${Size(f.SizeBytes)} · uploaded ${when}` : `Uploaded ${when}`;
}

/** logic.js:546 draftStatus, extended to the real phases (task 25 brief). */
function draftStatus(i: PresentationInputs): { Label: string; Tone: OEPTone } {
    const d = i.Timeline.Dates;
    if (StageOf(i.Phase) === 'sum') return { Label: `Upload opens ${d.UploadOpens.Label}`, Tone: 'neutral' };
    if (i.Phase === 'DraftUpload') return i.Draft ? { Label: 'Draft received', Tone: 'ok' } : { Label: 'Awaiting upload', Tone: 'warn' };
    // After the draft deadline, before release (2026-10-19 §5.3: DraftClosed, Matched, Reviewing).
    if (i.Phase === 'DraftClosed' || i.Phase === 'Matched' || i.Phase === 'Reviewing') return i.Draft ? { Label: 'Draft received', Tone: 'ok' } : { Label: 'No draft uploaded', Tone: 'hot' };
    if (i.Phase === 'FeedbackReleased') return { Label: 'Feedback ready', Tone: 'ok' };
    return { Label: `Presented at ${i.SeasonCode}`, Tone: 'ok' };
}

/** logic.js:586-589 presenterIntro. */
function intro(i: PresentationInputs): string {
    const stage = StageOf(i.Phase);
    if (stage === 'jan') return 'Your draft, your reviewer feedback and the final version you presented, all in one place.';
    if (stage === 'dec') return `Read your reviewer feedback, then upload the final version you are presenting at ${i.SeasonCode}.`;
    return `Upload your presentation draft for feedback. In late November, you will receive feedback from your reviewers, which you can use to adjust your presentation before ${i.SeasonCode}.`;
}

/** logic.js:563-567 threadNote; CanWrite mirrors DecideThreadMessage (3 messages, alternating, presenter starts). */
function thread(i: PresentationInputs, r: OEPReviewInput): { Note: string; CanWrite: boolean } {
    const n = r.Thread.length;
    const full = n >= THREAD_LIMIT;
    const mine = n > 0 && Up(r.Thread[n - 1].SenderUserID) === Up(i.Me);
    const open = IsWindowOpen('Thread', i.Phase);
    // Closed window (not reachable while reviews are visible today — the Thread window spans every released
    // phase — but never prompt a write the server refuses): the server's own refusal copy, decisions.ts:102.
    const note = full ? 'This conversation is complete. Three messages is the limit.'
        : !open ? 'Conversations open once feedback is released.'
        : mine ? 'Sent. Your reviewer can reply once more.'
        : n > 0 ? 'One more message each, then the thread closes.'
        : 'You can start one conversation with this reviewer. Three messages total.';
    return { Note: note, CanWrite: open && !full && !mine };
}

function feedbackItem(i: PresentationInputs, r: OEPReviewInput, idx: number, total: number): FeedbackItemVM {
    const t = thread(i, r);
    const scores = [...r.Scores]
        .filter((s) => s.Sequence >= 1 && s.Sequence <= CRITERION_SHORT.length)
        .sort((a, b) => a.Sequence - b.Sequence)
        .map((s) => ({ Label: CRITERION_SHORT[s.Sequence - 1], Value: s.Level !== null ? `${s.LevelLabel} (${s.Level})` : 'Not scored', Text: s.Text }));
    return {
        ReviewID: r.ReviewID, ConversationID: r.ConversationID,
        Who: r.ReviewerName, Institution: r.Institution,
        Position: `${idx + 1} of ${total}`, // logic.js:555 ofTwo
        When: `Released ${r.ReleasedOn ? DayMonth(r.ReleasedOn) : i.Timeline.Dates.FeedbackRelease.Label}`, // logic.js:417 when
        Scores: scores,
        Strengths: r.Strengths,
        Suggestions: r.Suggestions.trim().length > 0 ? r.Suggestions : null,
        ThreadNote: t.Note,
        Thread: r.Thread.map((m) => {
            const me = Up(m.SenderUserID) === Up(i.Me);
            return { Who: me ? 'You' : r.ReviewerName, Body: m.Body, Mine: me }; // logic.js:557
        }),
        CanWrite: t.CanWrite,
        ThanksTitle: `Thank ${r.ReviewerName}`, // markup.html:447
        // logic.js:570-572 thanksNote
        ThanksNote: r.ThankYouSent ? 'Sent. It travels with this reviewer’s acknowledgement letter from AGU.' : 'Please submit a thank you note to this reviewer. It means a lot.',
        ThanksSent: r.ThankYouSent,
        CanThank: !r.ThankYouSent && IsWindowOpen('ThankYou', i.Phase),
    };
}

/** markup.html:301-491 / logic.js:541-610 as one pure function. Every string cites its prototype line. */
export function BuildPresentationVM(i: PresentationInputs): PresentationVM {
    const d = i.Timeline.Dates;
    const stage = StageOf(i.Phase);
    const released = IsPhaseAtLeast(i.Phase, 'FeedbackReleased');
    const draftOpen = IsWindowOpen('DraftUpload', i.Phase);
    const finalOpen = IsWindowOpen('FinalUpload', i.Phase);
    const chosen = IsOEPCareerStage(i.CareerStage) ? i.CareerStage : null;
    // Links still on the placeholder Url are hidden from presenters (client edit 2026-10-27).
    const presenter = PublishedResources(i.Resources);
    const guidanceLinkTitle = 'How do I get feedback on my draft presentation?';
    const guidanceLink = presenter.find((r) => r.Title === guidanceLinkTitle) ?? null;

    const draftDrop = { Title: 'Upload your draft presentation', Note: `PDF, PowerPoint, poster image or video, up to 100 MB. This is the only file reviewers see. You can replace it any time before ${d.DraftDue.Label}.` }; // markup.html:340-341
    const finalDrop = { Title: 'Upload your final presentation', Note: 'PDF, PowerPoint, poster image or video, up to 100 MB.' }; // markup.html:372-373
    const eyebrow = [i.Presentation.Format, i.Presentation.Code].filter((x): x is string => x !== null && x.length > 0).join(' · ');
    return {
        Title: i.Presentation.Title,
        Intro: intro(i),
        // markup.html:308; client edit 2026-10-20: "Biogeosciences · Poster · B31C-0412"
        Eyebrow: eyebrow.length > 0 ? `${i.Presentation.SectionName} · ${eyebrow}` : i.Presentation.SectionName,
        DraftStatus: draftStatus(i),
        // markup.html:318-325
        HeadStart: stage === 'sum' ? {
            Text: `Your draft is due ${d.DraftDue.Label}. Read the prep guidance now. It tells you exactly what your two reviewers will be looking at, and what they will not.`,
            Links: presenter.slice(0, 2),
        } : null,
        // markup.html:328-336, logic.js:585 careerNote
        CareerAsk: IsWindowOpen('CareerStage', i.Phase)
            ? { Stages: OEP_CAREER_STAGES, Chosen: chosen, Note: 'First, tell us your career stage. Then upload your draft.' }
            : null,
        // markup.html:339-342
        DraftDrop: draftOpen && !i.Draft ? { ...draftDrop, Disabled: chosen === null, Hint: chosen === null ? OEP_CAREER_FIRST : null } : null,
        // markup.html:345-353 (note: copy change approved — presenters cannot see assignment counts before release, T5)
        DraftFile: i.Draft && !released ? {
            FileID: i.Draft.FileID, Name: i.Draft.Name, Detail: OEPFileDetail(i.Draft), CanReplace: draftOpen, Replace: draftOpen ? draftDrop : null,
            Note: draftOpen || !IsPhaseAtLeast(i.Phase, 'Matched')
                ? `Reviewers will see your presentation after ${d.DraftDue.Label}. Their feedback is released to you on ${d.FeedbackRelease.Label}.`
                : `Your matched reviewers can see your draft now. Their feedback is released to you on ${d.FeedbackRelease.Label}.`,
        } : null,
        // markup.html:359
        ClosedNote: released && i.Draft ? `Draft submitted ${DayMonth(i.Draft.UploadedAt)}. Edits closed ${d.DraftDue.Label}.` : null,
        // markup.html:364-392, logic.js:597-600
        Final: released ? {
            DeadlineNote: finalOpen ? `Due ${d.FinalDue.Label}` : `Uploads closed ${d.FinalDue.Label}`,
            Intro: finalOpen
                ? `Upload the final version of your presentation here. Nominators and your section’s awards committee may review this file when selecting winners. Due ${d.FinalDue.Label}.`
                : 'This is the version your session nominator and your section committee saw when awards were decided.',
            Drop: finalOpen && !i.Final ? finalDrop : null,
            Missed: !finalOpen && !i.Final,
            File: i.Final ? { FileID: i.Final.FileID, Name: i.Final.Name, Detail: OEPFileDetail(i.Final), CanReplace: finalOpen, Replace: finalOpen ? finalDrop : null } : null,
        } : null,
        // markup.html:396-458
        Feedback: i.Reviews.length > 0 ? {
            CountLabel: `${i.Reviews.length} ${i.Reviews.length === 1 ? 'reviewer' : 'reviewers'}`,
            Intro: `Review the feedback on your draft presentation here. You can use this feedback to strengthen your presentation ahead of ${i.SeasonCode}. Please remember to take a moment and thank your reviewers for their feedback.`,
            Items: i.Reviews.map((r, idx) => feedbackItem(i, r, idx, i.Reviews.length)),
        } : null,
        // markup.html:461-468
        Guidance: guidanceLink ? {
            Title: 'Information about the program',
            Text: 'How to upload your draft, timing, and the rubric your reviewers will use to provide feedback and nominators will use to nominate awardees. Read it before you upload.',
            Link: guidanceLink,
        } : null,
        // markup.html:470-474, logic.js:602-607 (winner / nonwinner; the pending variant and demo switcher are not shown)
        Outcome: i.Phase === 'Complete' && i.IsWinner !== null ? (i.IsWinner ? {
            Title: `You are an Outstanding Emerging Presenter for ${i.SeasonCode}`,
            Body: `The ${i.Presentation.SectionName} section committee selected your presentation. Your certificate, signed by the section president, is attached to the email we just sent, and your name goes on the published list of ${i.SeasonCode} winners.`,
        } : {
            Title: `Thank you for presenting at ${i.SeasonCode}`,
            Body: `Awards went to a small number of presentations in ${i.Presentation.SectionName} this year. Your draft, your reviewers’ feedback, and the fact that you stood up and presented all still count. Here are the AGU member resources that help most at your stage.`,
        }) : null,
    };
}
